import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { useLazyGetCurrentUserQuery } from 'api/main';
import { useSendStatutsMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { checkAcceptedFileTypes, setTunnelChangesHandlers, validateFileSize } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import TunnelBox from '../common/TunnelBox';
import TunnelStep from '../common/TunnelStep';
import { ActionButton, FileErrorMessage, FileInputButton, Loader, SubmitButton } from 'components';
import { Box, Typography } from '@mui/material';

const Statuts = props => {
    const { userStep, handleNextStep } = props;

    const [getCurrentUser, { isFetching: userLoading }] = useLazyGetCurrentUserQuery();
    const [
        sendStatuts,
        { isLoading: statutsLoader, isSuccess, reset, isError, error: submitError },
    ] = useSendStatutsMutation();

    const internalUser = useSelector(userSelectors.getInternalUser);

    const isResubmit = useMemo(() => !!userStep, [userStep]);
    const documentName = useMemo(
        () => internalUser?.investorProfile?.statutes?.name || null,
        [internalUser]
    );

    const [uploadedFile, setUploadedFile] = useState(null);
    const [lastUploadedFileName, setLastUploadedFileName] = useState(documentName);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [fileSizeError, setFileSizeError] = useState(false);

    useEffect(() => {
        setLastUploadedFileName(documentName);
    }, [documentName]);

    useEffect(() => {
        getCurrentUser();
    }, [getCurrentUser]);

    const handleFileChange = useCallback(
        ({ target: { files } }) => {
            const selectedFile = files[0];
            checkAcceptedFileTypes(
                selectedFile,
                () => {
                    setFileTypeError(false);
                    if (validateFileSize(selectedFile)) {
                        setUploadedFile(selectedFile);
                        setLastUploadedFileName(selectedFile?.name);
                        setFileSizeError(false);
                        reset();
                    } else {
                        setFileSizeError(true);
                    }
                },
                () => {
                    setFileTypeError(true);
                    setUploadedFile(null);
                    setFileSizeError(false);
                }
            );
        },
        [reset]
    );

    const handleSendFile = useCallback(
        afterSubmitCallback => () => {
            let formData = new FormData();
            formData.append('file', uploadedFile);
            sendStatuts({ payload: formData, callback: afterSubmitCallback });
        },
        [sendStatuts, uploadedFile]
    );

    useEffect(() => {
        if (isError) {
            if (submitError.status === 415) {
                setUploadedFile(null);
                setFileSizeError(true);
            } else {
                setUploadedFile(null);
                setFileTypeError(true);
            }
        }
    }, [isError, submitError]);

    const checkChanges = useCallback(
        () => !isSuccess && uploadedFile !== null,
        [isSuccess, uploadedFile]
    );

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSendFile, checkChanges, isResubmit),
        [handleSendFile, checkChanges, isResubmit]
    );

    return (
        <TunnelStep
            title={texts.STATUTS}
            subtitle={
                <Typography variant="body1">
                    <i>{texts.DISCONNECT_REMINDER}</i>
                </Typography>
            }
        >
            <Loader loading={userLoading}>
                <TunnelBox>
                    <Typography gutterBottom>{texts.MERCI_STATUTS}</Typography>
                    <Box pt={3} display="flex" flexWrap="wrap" justifyContent="center">
                        {fileTypeError && <FileErrorMessage variant="type" />}
                        {fileSizeError && <FileErrorMessage variant="size" />}{' '}
                        {!fileTypeError && !fileSizeError && (
                            <Typography
                                sx={{ width: '100%', mb: 2, textAlign: 'center' }}
                                variant="body2"
                            >{`${texts.UPLOADED_FILE_NAME} : ${
                                lastUploadedFileName || texts.AUCUN
                            }`}</Typography>
                        )}
                        <FileInputButton
                            id="statuts"
                            showIcon
                            label={texts.UPLOAD_FILE_BUTTON}
                            sx={{ width: '45%', display: 'flex', justifyContent: 'center' }}
                            onFileChange={handleFileChange}
                        />
                        <Box
                            sx={theme => ({
                                width: '45%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                [theme.breakpoints.down('sm')]: {
                                    width: '100%',
                                    marginTop: 3,
                                },
                            })}
                        >
                            <ActionButton
                                sx={{ padding: '10px 16px' }}
                                variant="contained"
                                onClick={handleSendFile(() => handleNextStep(true))}
                                disabled={!uploadedFile || statutsLoader}
                                loading={statutsLoader}
                            >
                                {texts.ENVOYER}
                            </ActionButton>
                        </Box>
                    </Box>
                </TunnelBox>
                <SubmitButton
                    text={isResubmit && changesMade ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                    onClick={handleNextStep}
                    disabled={
                        (isResubmit && !!uploadedFile && !isSuccess) || (!isResubmit && !isSuccess)
                    }
                />
            </Loader>
        </TunnelStep>
    );
};

Statuts.propTypes = {
    userStep: PropTypes.object,
    handleNextStep: PropTypes.func,
};

export default Statuts;
